import { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaSave, FaTimes } from 'react-icons/fa';
import partidoService from '../../services/partidoService';
import venueService from '../../services/venueService';
import { toast } from 'react-toastify';

/**
 * EditarCampoPartido (C-01 multi-campo)
 *
 * Modal para asignar o cambiar la instalación física (campo) de un partido.
 *
 * Comportamiento:
 * - Si la sede del partido no tiene campos definidos → avisa al admin.
 * - Si tiene campos → muestra selector con "Sin campo específico" + lista de activos.
 * - Guarda campo_id (o null) en partido vía PUT /api/partidos/:id/campo.
 *
 * Props:
 *   show         {boolean}   - Controla visibilidad
 *   onHide       {Function}  - Cierra el modal
 *   partido      {Object}    - Partido a editar (necesita partido_id y sede_id)
 *   onPartidoUpdated {Function} - Callback con el partido actualizado
 */
const EditarCampoPartido = ({ show, onHide, partido, onPartidoUpdated }) => {
  const [campoSeleccionado, setCampoSeleccionado] = useState('');
  const [campos, setCampos] = useState([]);
  const [loadingCampos, setLoadingCampos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cargar campos cuando se abre el modal
  useEffect(() => {
    if (show && partido?.sede_id) {
      cargarCampos(partido.sede_id);
      // Preseleccionar el campo actual si lo tiene
      setCampoSeleccionado(partido.campo_id ? String(partido.campo_id) : '');
      setError('');
    }
  }, [show, partido]);

  // Limpiar al cerrar
  useEffect(() => {
    if (!show) {
      setCampos([]);
      setCampoSeleccionado('');
      setError('');
      setLoading(false);
    }
  }, [show]);

  const cargarCampos = async (sedeId) => {
    setLoadingCampos(true);
    try {
      const res = await venueService.getCamposBySede(sedeId);
      setCampos(res.data || []);
    } catch (err) {
      console.error('Error cargando campos:', err);
      setCampos([]);
    } finally {
      setLoadingCampos(false);
    }
  };

  const handleGuardar = async () => {
    setLoading(true);
    setError('');
    try {
      const campoId = campoSeleccionado ? parseInt(campoSeleccionado) : null;
      const response = await partidoService.updateCampoPartido(partido.partido_id, campoId);

      if (response.success) {
        const campoAsignado = campos.find(c => c.id === campoId);
        toast.success(
          campoId
            ? `✅ Campo asignado: ${campoAsignado?.nombre}`
            : '✅ Campo desasignado. Se usará la sede de la jornada.'
        );

        if (onPartidoUpdated) {
          onPartidoUpdated({
            ...partido,
            campo_id: campoId,
            campo_nombre: campoAsignado?.nombre || null,
            campo_direccion: campoAsignado?.direccion || null
          });
        }

        onHide();
      } else {
        throw new Error(response.message || 'Error desconocido');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error desconocido';
      setError(msg);
      toast.error(`Error al asignar campo: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCampoSeleccionado('');
    setError('');
    setLoading(false);
    onHide();
  };

  if (!show || !partido) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100%', height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        backgroundColor: '#fff', borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        width: '100%', maxWidth: '480px',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa', position: 'relative'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: 0, paddingRight: '2rem' }}>
            <FaMapMarkerAlt style={{ marginRight: '0.5rem', color: '#007bff' }} />
            Asignar instalación al partido
          </h2>
          <button onClick={handleClose} style={{
            position: 'absolute', right: '1rem', top: '50%',
            transform: 'translateY(-50%)', background: 'none', border: 'none',
            fontSize: '24px', cursor: 'pointer', color: '#6c757d'
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {error && (
            <Alert variant="danger" className="mb-3">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {/* Info del partido */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-2 text-primary">📋 Partido #{partido.partido_id}</h6>
            <div className="small text-muted">
              <div><strong>Modalidad:</strong> F{partido.modalidad === 1 ? '8' : '11'}</div>
              <div><strong>Hora:</strong> {partido.hora || 'Sin hora'}</div>
              <div><strong>Campo actual:</strong> {partido.campo_nombre || 'Sin campo específico (usa sede de jornada)'}</div>
            </div>
          </div>

          {loadingCampos ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando instalaciones...
            </div>
          ) : campos.length === 0 ? (
            <Alert variant="warning">
              <strong>Esta sede no tiene instalaciones definidas.</strong><br />
              <small>Ve a <em>Gestión de Sedes</em> &rarr; detalle de la sede &rarr; sección &quot;Instalaciones&quot; para añadir campos.</small>
            </Alert>
          ) : (
            <Form.Group>
              <Form.Label style={{ fontWeight: '600' }}>
                <FaMapMarkerAlt className="me-1 text-primary" />
                Instalación / Campo
              </Form.Label>
              <Form.Select
                value={campoSeleccionado}
                onChange={(e) => setCampoSeleccionado(e.target.value)}
                disabled={loading}
                size="sm"
              >
                <option value="">— Sin campo específico (sede de jornada) —</option>
                {campos.map(campo => (
                  <option key={campo.id} value={String(campo.id)}>
                    {campo.nombre}
                    {campo.direccion ? ` · ${campo.direccion}` : ''}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Si no seleccionas campo, el partido mostrará la sede de la jornada como ubicación.
              </Form.Text>
            </Form.Group>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem', borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa', display: 'flex',
          justifyContent: 'flex-end', gap: '0.5rem'
        }}>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            <FaTimes className="me-1" /> Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleGuardar}
            disabled={loading || campos.length === 0}
          >
            {loading ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Guardando...</>
            ) : (
              <><FaSave className="me-1" /> Guardar</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditarCampoPartido;
